import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Loader2, ShieldAlert } from 'lucide-react';
import { useUserData } from './UserDataContext';
import { MessagingService } from '../services/api';

interface WatcherChatInspectorModalProps {
    chatId: string;
    /** Who the report was filed against, for the header. Taken from the report. */
    label: string;
    onClose: () => void;
}

/**
 * Read-only transcript of a reported conversation, for moderators.
 *
 * This exists because navigating a Watcher to DirectMessageScreen cannot work,
 * and twice looked like it did. That screen resolves its conversation with
 * getChat(friend.id) against MessagingContext, which subscribes to
 * subscribeToUserChats(firebaseUser.uid) — the viewer's OWN chats. A Watcher is
 * not a participant in the reported conversation, so the lookup returns
 * undefined no matter what ids are passed through the navigation event, and the
 * screen renders its "Start a conversation" empty state. The bug is in the
 * destination, not the payload, so no amount of enriching the event fixes it.
 *
 * Fetching straight from chats/{chatId}/messages sidesteps the whole context.
 * MessagingService.subscribeToMessages takes only a chatId and applies no
 * participant check, and it is an onSnapshot, so a conversation that continues
 * while a moderator watches updates live.
 *
 * A transcript viewer and nothing else: no composer, no edit or delete, no
 * request banners. A moderator reviews what was said; they do not join in.
 */
export function WatcherChatInspectorModal({ chatId, label, onClose }: WatcherChatInspectorModalProps) {
    const { userProfile } = useUserData();
    const [messages, setMessages] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const bottomRef = useRef<HTMLDivElement>(null);

    // The same gate WatcherRemote applies before rendering the panel at all,
    // rather than a second mechanism that could drift from it.
    const rank = String(userProfile?.rank || '').toLowerCase();
    const isModerator = rank === 'watcher' || rank === 'legion';

    useEffect(() => {
        if (!isModerator || !chatId) return;
        let cancelled = false;
        let unsub: (() => void) | null = null;

        (async () => {
            try {
                const off = await MessagingService.subscribeToMessages(chatId, (msgs) => {
                    if (cancelled) return;
                    setMessages(msgs);
                    setLoading(false);
                });
                if (cancelled) off(); else unsub = off;
            } catch (e) {
                console.error('[WatcherChatInspector] subscription failed:', e);
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; unsub?.(); };
    }, [chatId, isModerator]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages.length]);

    if (!isModerator) return null;

    const describe = (m: any): string => {
        if (m.text) return m.text;
        if (m.imageUrl) return '[Image]';
        if (m.audioUrl) return '[Voice]';
        if (m.videoUrl) return '[Video]';
        if (m.sharedYap) return '[Shared Yap]';
        if (m.groupInvite) return '[Group Invite]';
        return '[No content]';
    };

    const when = (ms: any): string => {
        const n = typeof ms === 'number' ? ms : Number(ms);
        if (!Number.isFinite(n)) return '';
        return new Date(n).toLocaleString([], {
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    };

    return createPortal(
        <AnimatePresence>
            <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0"
                    style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)' }}
                />
                <motion.div
                    initial={{ scale: 0.95, opacity: 0, y: 16 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 16 }}
                    className="relative w-full max-w-md rounded-2xl overflow-hidden flex flex-col"
                    style={{
                        background: '#111',
                        border: '1px solid rgba(220,38,38,0.3)',
                        maxHeight: '78vh',
                    }}
                >
                    <div
                        className="px-3 flex items-center justify-between shrink-0"
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', paddingTop: 10, paddingBottom: 10 }}
                    >
                        <div className="min-w-0">
                            <p className="text-white truncate" style={{ fontSize: 13, fontWeight: 700 }}>
                                Conversation with @{String(label).replace(/^@/, '')}
                            </p>
                            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
                                Read-only transcript
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                            style={{ background: 'rgba(255,255,255,0.08)' }}
                            aria-label="Close"
                        >
                            <X className="w-4 h-4 text-white" />
                        </button>
                    </div>

                    <div
                        className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-2"
                        style={{ minHeight: 0 }}
                    >
                        {loading ? (
                            <div className="flex justify-center" style={{ paddingTop: 32, paddingBottom: 32 }}>
                                <motion.div
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                >
                                    <Loader2 className="w-5 h-5" style={{ color: '#DC2626' }} />
                                </motion.div>
                            </div>
                        ) : messages.length === 0 ? (
                            <div className="text-center" style={{ paddingTop: 32, paddingBottom: 32 }}>
                                <ShieldAlert
                                    className="w-8 h-8 mx-auto mb-2"
                                    style={{ color: 'rgba(255,255,255,0.25)' }}
                                />
                                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                                    This conversation has no messages.
                                </p>
                            </div>
                        ) : (
                            messages.map((m) => {
                                // The reported user's messages are the ones under
                                // review, so they are the ones set apart. Neither
                                // side is "you" — a moderator is in neither seat.
                                const isReported = String(m.senderId || '') === String(label) ||
                                    String(m.senderName || '') === String(label);
                                return (
                                    <div
                                        key={m.id}
                                        className="rounded-xl px-3 py-2"
                                        style={{
                                            background: isReported
                                                ? 'rgba(220,38,38,0.12)'
                                                : 'rgba(255,255,255,0.05)',
                                            border: '1px solid rgba(255,255,255,0.06)',
                                        }}
                                    >
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                            <span
                                                className="truncate"
                                                style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.55)' }}
                                            >
                                                {m.senderId || 'unknown sender'}
                                            </span>
                                            <span
                                                className="shrink-0"
                                                style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}
                                            >
                                                {when(m.timestamp)}
                                            </span>
                                        </div>
                                        <p
                                            className="whitespace-pre-wrap"
                                            style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', overflowWrap: 'anywhere' }}
                                        >
                                            {describe(m)}
                                        </p>
                                    </div>
                                );
                            })
                        )}
                        <div ref={bottomRef} />
                    </div>

                    <div
                        className="px-3 py-2 shrink-0"
                        style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
                    >
                        <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
                            {messages.length} message{messages.length === 1 ? '' : 's'} · updates live
                        </p>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>,
        document.body,
    );
}

export default WatcherChatInspectorModal;
